
import { useEffect, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';

export const MAX_NONLOGGED_USES = 5; // Vous pouvez ajuster cette valeur selon vos besoins
const NONLOGGED_STORAGE_KEY = 'aluna-nonlogged-tool-usages';

export type IncrementUsageResult = {
  success: boolean;
  limitReached: boolean;
  reason: "anti-spam" | "quota" | null;
};

export function useNonLoggedToolUsage(toolSlug: string) {
  const [uses, setUses] = useState<number>(0);
  const { user } = useAuth();

  // Charger les données depuis localStorage
  useEffect(() => {
    if (user) {
      // Utilisateur connecté, pas de limite
      setUses(0);
      return;
    }
    
    const stored = localStorage.getItem(NONLOGGED_STORAGE_KEY);
    const parsed = stored ? JSON.parse(stored) : {};
    setUses(parsed[toolSlug] || 0);
    console.log(`[useNonLoggedToolUsage] Initialisation - Outil: ${toolSlug}, Utilisations: ${parsed[toolSlug] || 0}`);
  }, [toolSlug, user]);

  const incrementUsage = (): IncrementUsageResult => {
    // Si connecté, ne pas incrémenter et autoriser l'action
    if (user) return { success: true, limitReached: false, reason: null };
    
    const now = Date.now();
    const lastAction = parseInt(localStorage.getItem('aluna-last-action-timestamp') || '0', 10);

    // anti-spam dans les 2s
    if (now - lastAction < 2000) {
      console.log(`[useNonLoggedToolUsage] Anti-spam activé - Dernière action il y a moins de 2s`);
      return { success: false, limitReached: false, reason: "anti-spam" };
    }

    const stored = localStorage.getItem(NONLOGGED_STORAGE_KEY);
    const parsed = stored ? JSON.parse(stored) : {};
    const current = parsed[toolSlug] || 0;
    const newCount = current + 1;

    // Vérifier si la limite est atteinte
    if (newCount > MAX_NONLOGGED_USES) {
      console.log(`[useNonLoggedToolUsage] Limite atteinte - Outil: ${toolSlug}, Compteur: ${current}`);
      return { success: false, limitReached: true, reason: "quota" };
    }

    parsed[toolSlug] = newCount;
    localStorage.setItem(NONLOGGED_STORAGE_KEY, JSON.stringify(parsed));
    localStorage.setItem('aluna-last-action-timestamp', now.toString());

    console.log(`[useNonLoggedToolUsage] Incrémentation - Outil: ${toolSlug}, Nouveau compteur: ${newCount}`);
    setUses(newCount); // force re-render
    
    return { 
      success: true, 
      limitReached: newCount >= MAX_NONLOGGED_USES,
      reason: null
    };
  };

  // Log à chaque render pour suivre les updates
  console.log(`[useNonLoggedToolUsage] Render - Outil: ${toolSlug}, Compteur actuel: ${uses}`);

  return {
    uses,
    remaining: Math.max(0, MAX_NONLOGGED_USES - uses),
    isLimitReached: uses >= MAX_NONLOGGED_USES,
    incrementUsage,
  };
}
